// frontend/src/components/NotificationBell.tsx
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useQuery } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchRenewals } from "../lib/dataFetchers";
import { cn } from "../lib/cn";
import { RENEWAL_DUE_SOON_DAYS } from "../lib/severity";
import type { RenewalEventSeverity } from "../lib/types";

// 3-tier severity; bell shows only red (urgent) items.
// Typed against RenewalEventSeverity so tsc catches any future enum widening.
const SEV_STYLES: Partial<Record<RenewalEventSeverity, string>> = {
  red: "bg-red-500 text-white",
};

export function NotificationBell(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation("bell");

  const { data: events = [] } = useQuery({
    queryKey: ["renewals"],
    queryFn: () => fetchRenewals(),
    staleTime: 60_000,
  });

  // Bell shows ONLY urgent items: severity "red" or daysUntil ≤ due-soon (belt-and-braces fallback).
  const isUrgent = (e: { severity: string; daysUntil: number }) =>
    e.severity === "red" || e.daysUntil <= RENEWAL_DUE_SOON_DAYS;

  const urgent = events.filter(isUrgent).slice(0, 5);
  const count = events.filter(isUrgent).length;

  const handleNavigateToEntity = (entityId: string) => {
    navigate(`/entities/${entityId}`);
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={t("aria_label")}
          className={cn(
            "relative h-11 w-11 inline-flex items-center justify-center",
            "rounded-lg hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-accent/20",
          )}
        >
          <Bell className="h-5 w-5 text-text" aria-hidden />
          {count > 0 && (
            <span
              aria-live="polite"
              className={cn(
                "absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1",
                "text-white text-[10px] font-bold bg-sev-expired",
                "rounded-full flex items-center justify-center leading-none",
              )}
            >
              {count}
            </span>
          )}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          sideOffset={6}
          align="end"
          className={cn(
            "z-50 w-[min(380px,94vw)] bg-surface border border-border",
            "rounded-card shadow-card-hov py-2",
            "animate-in fade-in-0 zoom-in-95",
          )}
        >
          {/* Header row */}
          <div className="px-3 pb-1.5 border-b border-border mb-1">
            <p className="text-[11px] uppercase tracking-wide text-dim font-semibold">
              {t("header", { count })}
            </p>
          </div>

          {/* Body */}
          {urgent.length === 0 ? (
            <p className="px-3 py-3 text-sm text-muted">
              {t("no_urgent")}
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {urgent.map((ev) => {
                return (
                  <li
                    key={ev.id}
                    className="flex items-center gap-2 px-3 py-2.5 hover:bg-surface-2"
                  >
                    <DropdownMenu.Item asChild>
                      <button
                        type="button"
                        onClick={() => handleNavigateToEntity(ev.entityId)}
                        className="flex items-center gap-2 flex-1 min-w-0 text-left focus:outline-none"
                      >
                        <span className="text-base leading-none" aria-hidden>
                          {ev.countryFlag}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-text truncate">
                            {ev.entityName}
                          </p>
                          <p className="text-xs text-muted truncate">
                            {ev.type === "License"
                              ? t("type_license")
                              : ev.type === "AnnualFiling"
                                ? t("type_annual_filing")
                                : ev.type === "EOR"
                                  ? t("type_eor")
                                  : t("type_company")}
                          </p>
                        </div>
                        <span
                          className={cn(
                            "text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0",
                            SEV_STYLES[ev.severity] ?? "bg-surface-2 text-dim",
                          )}
                        >
                          {ev.daysUntil < 0
                            ? `${Math.abs(ev.daysUntil)}d ago`
                            : `${ev.daysUntil}d`}
                        </span>
                      </button>
                    </DropdownMenu.Item>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Renewals footer */}
          <div className="px-3 pt-1.5 border-t border-border mt-1">
            <DropdownMenu.Item asChild>
              <button
                type="button"
                onClick={() => navigate("/renewals")}
                className="text-xs text-accent hover:underline min-h-[44px]"
              >
                {t("see_all")}
              </button>
            </DropdownMenu.Item>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
